import Head from 'next/head';
import { useRouter } from 'next/router';
import { type SSRConfig, useTranslation } from 'next-i18next';
import { useLocale } from '@/hooks';
import { isDevelopment, isStage } from '@/utils/environment';
import type { THIS_IS_DEFAULT_STATIC_OR_SERVER_SIDE_PROPS } from '@/utils/next';
import { type SeoProperties } from '../../types';
import { RequireDefaultPageProps } from './RequireDefaultPageProps';
const getTitle = (title: string, postTitle: string, isAdminPage: boolean) => {
  if (isAdminPage) return 'QUIZ ADMIN';
  if (title) return title;
  return `ZEP QUIZ | ${postTitle}`;
};
export type MetaProps = {
  pageProps: {
    seoContent: Partial<SeoProperties>;
    noIndex?: boolean;
    [THIS_IS_DEFAULT_STATIC_OR_SERVER_SIDE_PROPS]?: boolean;
    canonicalUrl?: string;
    ogUrl?: string;
  } & SSRConfig;
};
export const Meta = ({
  pageProps
}: MetaProps) => {
  const {
    seoContent: partialSeoContent,
    noIndex = false,
    canonicalUrl,
    ogUrl
  } = pageProps;
  const {
    asPath,
    pathname
  } = useRouter();
  const {
    locale,
    locales
  } = useLocale();
  // 번역 저장소(`pageProps._nextI18Next`)를 직접 읽지 않는다 — 데이터 페칭이 없는
  // 정적 셸에는 그게 아예 없어서 전 로케일이 아래 한국어 폴백으로 떨어졌다.
  // `ready` 전에는 t() 가 키를 돌려주므로 그때만 폴백을 쓴다.
  const {
    t,
    ready
  } = useTranslation('common');
  const isAdminPage = pathname.startsWith('/admin');
  const defaultPostTitle = ready ? t('seo.default.postTitle') : '나만의 젭 퀴즈를 만들어 더 재미있는 수업을 진행해 보세요';
  const title = getTitle(partialSeoContent?.title ?? '', defaultPostTitle, isAdminPage);
  const defaultDescription = ready ? t('seo.default.description') : '';
  const defaultImage = isDevelopment() ? 'https://dev-quiz.zep.us/assets/ogimage.png' : 'https://quiz.zep.us/assets/ogimage.png';
  const seoContent = {
    title,
    subject: partialSeoContent?.subject ?? title,
    description: partialSeoContent?.description ?? defaultDescription,
    image: partialSeoContent?.image ?? defaultImage,
    keywords: partialSeoContent?.keywords ?? 'ZEP, metaverse, zep quiz, meta, quiz, 젭, 젭퀴즈, 퀴즈 제작, 퀴즈 만들기, 방탈출퀴즈',
    copyright: partialSeoContent?.copyright ?? 'ZEP Inc',
    author: partialSeoContent?.author ?? '',
    schema: {
      image: defaultImage,
      ...partialSeoContent?.schema
    } as Record<string, any>
  };
  const fallbackOgPath = (() => {
    const currentPath = asPath === '/' ? '' : asPath;
    if (locale === 'ko') return currentPath;
    const localePrefix = `/${locale}`;
    if (!currentPath) return localePrefix;
    if (currentPath === localePrefix || currentPath.startsWith(`${localePrefix}/`)) {
      return currentPath;
    }
    return `${localePrefix}${currentPath}`;
  })();
  const resolvedOgUrl = ogUrl ?? `${process.env.NEXT_PUBLIC_BASE_URL}${fallbackOgPath}`;
  return <RequireDefaultPageProps pageProps={pageProps} data-sentry-element="RequireDefaultPageProps" data-sentry-component="Meta" data-sentry-source-file="Meta.tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="Meta.tsx">
        {locale === 'ko' && !noIndex && !canonicalUrl && <link rel="canonical" href={`${process.env.NEXT_PUBLIC_BASE_URL}${asPath === '/' ? '' : asPath}`} />}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {!['/quiz/[id]', '/profile/[userHashId]', '/play/[entryMapHashId]'].includes(pathname) && <>
            <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_PUBLIC_BASE_URL}${asPath === '/' ? '' : asPath}`} />
            {locales.map(lang => <link key={lang} rel="alternate" hrefLang={lang} href={`${process.env.NEXT_PUBLIC_BASE_URL}${lang === 'ko' ? '' : `/${lang}`}${asPath === '/' ? '' : asPath}`} />)}
          </>}
        <link rel="icon" type="image/svg+xml" href="/assets/ZEPQuiz-favicon.svg" />
        {isDevelopment() || isStage() || noIndex ? <>
            <meta name="robots" content="noindex" />
            <meta name="googlebot" content="noindex" />
          </> : <>
            <meta name="robots" content="index, follow" />
            <meta name="googlebot" content="index, follow" />
          </>}
        <meta name="google" content="notranslate" data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <title>{seoContent.title}</title>
        <meta name="title" content={seoContent.title} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta name="description" content={seoContent.description} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta name="subject" content={seoContent.subject} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta name="keywords" content={seoContent.keywords} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="og:type" content="website" data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="og:site_name" content={seoContent.title} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="og:url" content={resolvedOgUrl} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="og:title" content={seoContent.title} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="og:description" content={seoContent.description} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="og:image" content={seoContent.image} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="og:locale" content={locale} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="twitter:card" content="summary" data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="twitter:title" content={seoContent.title} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="twitter:description" content={seoContent.description} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta property="twitter:image" content={seoContent.image} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta name="application-name" content="ZEP Quiz" data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta name="copyright" content={seoContent.copyright} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta name="author" content={seoContent.author} data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        <meta name="reply-to" content="hello@zep.us" data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        {/* {process.env.NEXT_PUBLIC_STAGE === 'PRD' &&
          process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_CLIENT_ID && (
            <meta
              name="google-adsense-account"
              content={process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_CLIENT_ID}
            />
          )} */}
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" data-sentry-element="meta" data-sentry-source-file="Meta.tsx" />
        {seoContent.schema['@context'] && <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(seoContent.schema)
      }} />}
      </Head>
    </RequireDefaultPageProps>;
};